import type { Reel, Stage } from './domain.js';
import { STAGES } from './domain.js';

/**
 * Stage rules. A reel moves forward through Script, Record, Video, Post, Stats.
 * People can always go back to look and to edit the script or record again; the stage itself only
 * moves forward through these actions.
 */
export type ReelAction = 'approveScript' | 'startRender' | 'renderDone' | 'writePost' | 'publish';

const ADVANCE: Record<ReelAction, { from: Stage[]; to: Stage }> = {
  approveScript: { from: ['script', 'record', 'video', 'post'], to: 'record' },
  startRender: { from: ['record', 'video', 'post'], to: 'video' },
  renderDone: { from: ['video'], to: 'video' },
  writePost: { from: ['video', 'post'], to: 'post' },
  publish: { from: ['post'], to: 'stats' },
};

export function stageIndex(stage: Stage): number {
  return STAGES.indexOf(stage);
}

export class StageError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StageError';
  }
}

/** Returns the stage after `action`. A reel never moves backwards: approving again on Video keeps Video. */
export function nextStage(reel: Pick<Reel, 'stage'>, action: ReelAction): Stage {
  const rule = ADVANCE[action];
  if (!rule.from.includes(reel.stage)) {
    throw new StageError(`Cannot ${action} while the reel is in stage ${reel.stage}`);
  }
  return stageIndex(rule.to) > stageIndex(reel.stage) ? rule.to : reel.stage;
}

/** Posted: stage Stats, a stored post, or a confirmed publication (a late stage write cannot hide it). */
export function isPublished(reel: Pick<Reel, 'stage'> & Partial<Pick<Reel, 'post' | 'publication'>>): boolean {
  return reel.stage === 'stats' || !!reel.post || reel.publication?.state === 'published';
}

/** Script edits are allowed until the reel is posted. */
export function canEditScript(
  reel: Pick<Reel, 'stage' | 'job'> & Partial<Pick<Reel, 'post' | 'publication'>>,
): boolean {
  return !isPublished(reel) && !(reel.job?.status === 'running' && reel.job.kind === 'script');
}

/** The video is out of date when the script or the chosen take changed after the render. */
export function isRenderStale(reel: Pick<Reel, 'render' | 'selectedTake'>, scriptVersion: number): boolean {
  if (!reel.render) return false;
  return reel.render.scriptVersion !== scriptVersion || reel.render.take !== reel.selectedTake;
}
